import { skipToken } from "@reduxjs/toolkit/query";
import type { DateRange } from "@repo/app-types/entities/dateRange";
import type { PlaybookHasResultCondition } from "@repo/app-types/entities/filterConditions";
import type {
  PlaybookId,
  PlaybookView,
} from "@repo/app-types/entities/playbook";
import { useMemo } from "react";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import type { FilterDefaults } from "./filters.js";
import { stripPlaybookHasResultConditions } from "./playbookCondition.js";
import { resolvePlaybookViewFilterDefaults } from "./playbookViewFilterDefaults.js";
import { useCompanyDefaultDateRange } from "./useCompanyDefaultDateRange.js";

export function buildHasResultPlaybookCondition(
  playbookId: PlaybookId,
): PlaybookHasResultCondition {
  return { playbookId, kind: "has_result" };
}

/**
 * The playbook's default filters for one kind of page, as filter defaults.
 *
 * `playbookId` is optional because an agent's pages resolve their playbook from
 * the agent, so it is genuinely unknown for the first render or two. Until it
 * arrives — and for a playbook that has configured nothing — this contributes
 * nothing and the page keeps the app's own defaults.
 */
function usePlaybookViewFilterDefaults(
  playbookId: PlaybookId | undefined,
  view: PlaybookView,
): FilterDefaults {
  const { data: playbook } = usePlaybookByIdQuery(playbookId ?? skipToken);

  return useMemo(
    () => resolvePlaybookViewFilterDefaults(playbook, view),
    [playbook, view],
  );
}

/**
 * The filter defaults a page scoped to one playbook starts on, to hand to
 * `useFilters`. Resolved by the page rather than by `useFilters` itself: a
 * component can be mounted both inside and outside a playbook's pages (the
 * playbook panel appears on the teammate page and in an agent's Overview tab),
 * so which defaults apply is the caller's to state.
 *
 * The playbook's saved filters, plus its "has result" condition, which keeps
 * calls where every criterion came back N/A — voicemails and the like — out of
 * the page. That one is controlled by "Include calls without grades" in the
 * Playbook results menu rather than appearing as a removable chip, so a saved
 * set of playbook conditions carries the viewer's answer to that too and
 * replaces this default outright.
 */
export function usePlaybookFilterDefaults(
  playbookId: PlaybookId,
  view: PlaybookView,
): FilterDefaults {
  const storedDefaults = usePlaybookViewFilterDefaults(playbookId, view);

  return useMemo(
    () => ({
      playbookConditions: [buildHasResultPlaybookCondition(playbookId)],
      ...storedDefaults,
    }),
    [storedDefaults, playbookId],
  );
}

/**
 * The same, for the agents workspace, which has never filtered out the calls
 * its rubric found nothing to grade — an agent's own calls are the point of
 * those pages. It honours every other filter the playbook saved.
 */
export function useAgentPlaybookFilterDefaults(
  playbookId: PlaybookId | undefined,
  view: PlaybookView,
): FilterDefaults {
  const storedDefaults = usePlaybookViewFilterDefaults(playbookId, view);

  return useMemo(
    () =>
      storedDefaults.playbookConditions
        ? {
            ...storedDefaults,
            playbookConditions: stripPlaybookHasResultConditions(
              storedDefaults.playbookConditions,
            ),
          }
        : storedDefaults,
    [storedDefaults],
  );
}

/**
 * The date range a page starts on when it holds its own period state instead of
 * taking one from the filter context — the performance reports, which own their
 * window in SQL. Resolves the playbook's default, then the company's; the
 * caller supplies the final fallback.
 */
export function usePlaybookViewDefaultDateRange(
  playbookId: PlaybookId,
  view: PlaybookView,
): DateRange | undefined {
  const { dateRange } = usePlaybookViewFilterDefaults(playbookId, view);
  const companyDefaultDateRange = useCompanyDefaultDateRange();

  return dateRange ?? companyDefaultDateRange;
}

/**
 * Only the playbook's date defaults, for a page whose filter bar is the date
 * controls alone. Handing it the rest would narrow what the page counts with
 * nothing on screen to say so, and no chip to take back off.
 */
export function usePlaybookViewDateDefaults(
  playbookId: PlaybookId | undefined,
  view: PlaybookView,
): FilterDefaults {
  const storedDefaults = usePlaybookViewFilterDefaults(playbookId, view);

  return useMemo(
    () => ({
      ...(storedDefaults.dateRange
        ? { dateRange: storedDefaults.dateRange }
        : {}),
      ...(storedDefaults.dateBasis
        ? { dateBasis: storedDefaults.dateBasis }
        : {}),
    }),
    [storedDefaults],
  );
}
